/**
 * Workstation Antennas are a group af antennas connected to a workstation that start
 * and stop all at the same time and share read EPCs.
 *
 * @name WorkstationAntennas
 *
 * @implements {EpcReaderInterface}
 * @type {ngFactory}
 * @prop {boolean} $started If true, the antenna is started
 */
function WorkstationAntennasFactory(
  EpcReaderInterface,
  TmrConnectionTimeoutError,
  TmrDeviceManagerBadRequestError,
  TmrDeviceManagerConnectionTimeoutError,
  TmrDeviceManagerNotFoundError,
  TmrWorkstationAntennasEmptyError,
  Workstation,
  $log,
  TmrLocalStorage,
  UserStorage
) {
  'ngInject'

  function WorkstationAntennas(workstation) {
    if (!workstation instanceof Workstation) {
      throw new Error(
        'WorkstationAntennas constructor requires a Workstation resource'
      )
    }

    // Each antenna must send its own reader to the device manager, and the
    // reader must contain a map of all its antennas.
    //
    // Workstation
    //   rfidReaders{}       < Object of arrays of antennas keyed by their readers
    //   antennas[]          < Array of antennas of this workstation
    //     rfidReader        < The reader of this antenna
    //       rfidAntennas[]  < Array of antennas of rfidReader (Filled manually here)
    workstation.antennas.forEach(
      ({ rfidReader }) =>
        (rfidReader.rfidAntennas =
          rfidReader.rfidAntennas ?? workstation.rfidReaders[rfidReader.id])
    )

    Object.assign(
      this,
      { antennas: workstation.antennas },
      EpcReaderInterface.$defaultValues()
    )
  }

  Object.assign(WorkstationAntennas.prototype, EpcReaderInterface, {
    $start,
    $stop,
    $read,
    $write,
    $kill,
    $setItemService,
    $describe,
    $onError,
  })

  return WorkstationAntennas

  /**
   * Start all antennas.
   *
   * @method WorkstationAntennas#$start
   * @prop {object} startParams Parameters passed to the Antenna when starting. For available params, @see {@link Antenna.$start}
   * @prop {function=} filter An optional function that filters the antennas that must be started
   * @prop {function=} messageCallback A function that is executed when a tag is read. Use with care, as the function must then take care to call {@link EpcReaderInterface.$readEpc} manually.
   * @returns {Promise} An "all" promise that fulfills when all antennas are started.
   */
  function $start(
    startParams = {},
    filter = antennas => antennas,
    messageCallback
  ) {
    let antennas = filter(this.antennas)
    if (!antennas.length) throw new TmrWorkstationAntennasEmptyError()

    messageCallback =
      messageCallback ?? (message => this.$readEpc.call(this, message))
    this.$working = true

    // Apply default options, then relevant options from startParams.
    const defaultParams = (({ $decode, $loadProducts, $mode }) => ({
      $decode,
      $loadProducts,
      $mode,
    }))(EpcReaderInterface.$defaultValues())
    Object.assign(this, defaultParams, startParams)

    return Promise.all(
      antennas.map(antenna => antenna.$start(startParams, messageCallback))
    )
      .then(
        () => (this.$started = true),
        err => this.$onError(err)
      )
      .finally(() => (this.$working = false))
  }

  /**
   * Stop all antennas.
   *
   * @method WorkstationAntennas#$stop
   * @prop {function=} filter An optional function that filters the antennas that must be stopped
   * @returns {Promise} An "all" promise that fulfills when all antennas are stopped
   */
  function $stop(filter = antennas => antennas) {
    let antennas = filter(this.antennas)
    if (!antennas.length) throw new TmrWorkstationAntennasEmptyError()

    this.$working = true
    this.$started = false
    let promises = antennas.map(antenna => antenna.$stop())
    return Promise.all(promises)
      .then(() => this.$clearQueues())
      .finally(() => (this.$working = false))
  }

  /** @ignore */
  function readOrWrite(
    readOrWrite,
    operation,
    params,
    filter = antennas => antennas
  ) {
    switch (typeof operation) {
      // (operation), (operation, params), (operation, params, filter)
      case 'string':
        break

      // (params), (params, filter)
      case 'object':
        if (params !== undefined) filter = params
        params = operation
        operation = false
        break

      default:
        throw new Error('Missing required first argument: operation or params')
    }

    let antennas = filter(this.antennas)
    if (!antennas.length) throw new TmrWorkstationAntennasEmptyError()

    return (this.$started ? this.$stop() : Promise.resolve())
      .then(() => {
        this.$working = true
        return operation
          ? antennas[0][readOrWrite](operation, params)
          : antennas[0][readOrWrite](params)
      })
      .finally(() => (this.$working = false))
  }

  /**
   * Read a tag using the first antenna of the workstation.
   *
   * Note that this stops the antennas before reading.
   *
   * @example <caption>Using a preset from {@link tmrAntennaReadOperations}</caption>
   * antenna.$read('epc', { targetEpc: '123' })
   * @example <caption>Using custom parameters</caption>
   * antenna.$read({
   *   memoryBank: 'EPC',
   *   address: 2,
   *   numBlocks: 6,
   *   targetEpc: '123'
   * })
   *
   * @method WorkstationAntennas#$read
   * @prop {string} operation @see Antenna#$read
   * @prop {object|string} params @see Antenna#$read
   * @prop {function=} filter An optional function that filters the antennas that must read
   * @returns {Promise} A promise that fulfills when the operation is finished
   */
  function $read(operation, params, filter) {
    return readOrWrite.call(this, '$read', operation, params, filter)
  }

  /**
   * Write a tag using the first antenna of the workstation.
   *
   * Note that this stops the antennas before writing.
   *
   * @example <caption>Using a preset from {@link tmrAntennaReadOperations}</caption>
   * antenna.$write('epc', { targetEpc: 'OLD_EPC', data: 'NEW_EPC' })
   * @example <caption>Using custom parameters</caption>
   * antenna.$write({
   *   memoryBank: 'EPC',
   *   address: 2,
   *   numBlocks: 6,
   *   targetEpc: 'OLD_EPC',
   *   data: 'NEW_EPC'
   * })
   *
   * @method WorkstationAntennas#$write
   * @prop {string} operation @see Antenna#$write
   * @prop {object|string} params @see Antenna#$write
   * @prop {function=} filter An optional function that filters the antennas that must write
   * @returns {Promise} A promise that fulfills when the operation is finished
   */
  function $write(operation, params, filter) {
    return readOrWrite.call(this, '$write', operation, params, filter)
  }

  /**
   * Kill a tag using the first antenna of the workstation.
   *
   * Note that this stops the antennas before writing.
   *
   * @method WorkstationAntennas#$kill
   * @prop {string} operation @see Antenna#$kill
   * @prop {object|string} params @see Antenna#$kill
   * @prop {function=} filter An optional function that filters the antennas that must kill
   * @returns {Promise} A promise that fulfills when the operation is finished
   */
  function $kill(operation, params, filter) {
    return readOrWrite.call(this, '$kill', operation, params, filter)
  }

  /**
   * Set the TmrResource service that is used by the antenna, for decoding and other purposes.
   *
   * @method WorkstationAntennas#$setItemService
   */
  function $setItemService(itemService) {
    this.$itemService = itemService
    this.antennas.forEach(antenna => (antenna.$itemService = itemService))
  }

  /**
   * Outputs a translatable description of the device.
   *
   * @method WorkstationAntennas#$describe
   */
  function $describe() {
    return {
      count: this.antennas.length,
      readers: this.antennas.map(({ rfidReader }) => rfidReader.description),
    }
  }

  /**
   * Callback executed after an error while sending a request.
   *
   * @method Antenna#$onError
   */
  function $onError(err) {
    // Debug mode allows ignoring errors.
    if (
      UserStorage.isDebugger() &&
      TmrLocalStorage.get('ignoreDeviceManageErrors')
    ) {
      this.$started = true
      $log.error(err)
    } else {
      if (err instanceof TmrConnectionTimeoutError)
        throw new TmrDeviceManagerConnectionTimeoutError()
      else if (err.status == 400) throw new TmrDeviceManagerBadRequestError()
      else if (err.status == 404) throw new TmrDeviceManagerNotFoundError()
      else throw err
    }
  }
}

export const factory = ['WorkstationAntennas', WorkstationAntennasFactory]
